'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import type { Header as HeaderType } from '@/payload-types'

import { CMSLink } from '@/components/Link'
import Link from 'next/link'
import { MenuIcon } from 'lucide-react'
import { Logo } from '@/components/Logo/Logo'
import { cn } from '@/utilities/cn'
import { redirect, usePathname, useRouter } from 'next/navigation'
import { getLinkUrl } from '@/fields/link'
import { useUserContext } from '@/providers/UserProvider'
import { Button, ToggleButton } from '@/components/ui/button'

export const HeaderNav: React.FC<{ header: HeaderType }> = ({ header }) => {
  const navItems = header?.navItems || []
  const drawerItems = header?.drawerItems || []
  const [open, setOpen] = useState(false)
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const [drawerHeight, setDrawerHeight] = useState(0)
  const pathname = usePathname()
  const { user, signOut } = useUserContext()
  const router = useRouter()

  const defaultSubMenu = user?.role === 'Coach' ? 'coach' : user ? 'user' : 'site'
  const [subMenu, setSubMenu] = useState(defaultSubMenu as 'user' | 'coach' | 'site')

  // Update the drawer height whenever "open" changes
  useEffect(() => {
    if (open && drawerContentRef.current) {
      setDrawerHeight(drawerContentRef.current.clientHeight)
    } else {
      setDrawerHeight(0)
    }
  }, [open, subMenu])

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  const isActive = useCallback(
    (href: string) => {
      return pathname === href
    },
    [pathname],
  )

  async function onSignInOutClick() {
    try {
      if (user) {
        await signOut()
        router.push('/')
      } else {
        router.push('/inloggen')
      }
    } finally {
      setOpen(false)
    }
  }

  return (
    <div className={'bg-white'}>
      <div className="container relative h-16 flex items-center justify-between">
        <Link href="/">
          <Logo loading="eager" priority="high" />
        </Link>
        <div className={'flex items-center gap-4'}>
          <nav className="hidden md:flex gap-4 items-center">
            {navItems.map(({ link }, i) => {
              return <CMSLink key={i} {...link} />
            })}
            {/*<Link href="/search">*/}
            {/*  <span className="sr-only">Search</span>*/}
            {/*  <SearchIcon className="w-5 text-primary" />*/}
            {/*</Link>*/}
          </nav>
          <nav className={'flex'}>
            <MenuIcon
              className={'size-8 text-primary cursor-pointer'}
              onClick={() => setOpen(!open)}
            />
          </nav>
        </div>
      </div>

      <div
        className={cn(
          'flex transition-all relative overflow-clip',
          open && 'z-10 bg-white shadow-lg',
          'flex-col',
        )}
        style={{ height: drawerHeight + 'px' }}
      >
        <div className={'absolute w-full'}>
          <div ref={drawerContentRef} className={'p-2 pb-4 container max-w-4xl'}>
            <div className={'flex flex-col gap-2'}>
              {user && (
                <div className={'flex gap-2 justify-between'}>
                  <div>Welkom {user.name}</div>
                  <div className={'flex gap-2'}>
                    <ToggleButton
                      size={'sm'}
                      pressed={subMenu === 'site'}
                      onClick={() => setSubMenu('site')}
                    >
                      Site menu
                    </ToggleButton>
                    <ToggleButton
                      size={'sm'}
                      pressed={subMenu === 'user'}
                      onClick={() => setSubMenu('user')}
                    >
                      Gebruiker menu
                    </ToggleButton>
                    {(user?.role === 'Coach' || user?.role === 'Admin') && (
                      <ToggleButton
                        size={'sm'}
                        pressed={subMenu === 'coach'}
                        onClick={() => setSubMenu('coach')}
                      >
                        Coach menu
                      </ToggleButton>
                    )}
                  </div>
                </div>
              )}
              {subMenu === 'user' && (
                <>
                  <Link href={'/mijn/profiel'}>
                    <div
                      className={cn(
                        'px-4 py-2 border-2 border-transparent bg-stone-100 text-primary rounded-full font-semibold',
                        isActive('/dashboard') && 'border-primary bg-white',
                      )}
                    >
                      Mijn profiel
                    </div>
                  </Link>
                </>
              )}
              {subMenu === 'site' &&
                drawerItems.map(({ link }, i) => {
                  return (
                    <CMSLink key={i} {...link} label={''} appearance={'inline'}>
                      <div
                        className={cn(
                          'px-4 py-2 border-2 border-transparent bg-stone-100 text-primary rounded-full font-semibold',
                          isActive(getLinkUrl(link)) && 'border-primary bg-white',
                        )}
                      >
                        {link.label}
                      </div>
                    </CMSLink>
                  )
                })}
              {subMenu === 'coach' && (
                <>
                  <Link href={'/tickets/casting-q2/scanner'}>
                    <div
                      className={cn(
                        'px-4 py-2 border-2 border-transparent bg-stone-100 text-primary rounded-full font-semibold',
                        isActive('/dashboard') && 'border-primary bg-white',
                      )}
                    >
                      Ticket Scanner: Casting Q2
                    </div>
                  </Link>
                </>
              )}
              <div
                onClick={onSignInOutClick}
                className={cn(
                  'px-4 py-2 border-2 border-transparent bg-primary text-stone-100 rounded-full font-semibold cursor-pointer',
                  isActive('/dashboard') && 'border-primary bg-white',
                )}
              >
                {user ? 'Uitloggen' : 'Inloggen'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
